import type { NavGraph, Point3 } from './nav-graph';
import type { NavGrid } from './nav-grid';
import { GateVerdict, TrajectoryFilter, TrajectorySettings, WalkGate } from './person-motion';
import { levelOf } from './site-model-georef';
import { SiteModelLevel } from './site-model.types';

/** Ce que devient une position reçue pour le personnage 3D. */
export interface PlanStep {
  verdict: GateVerdict | 'duplicate';
  /** Position mesurée posée sur son étage, ou sur l'escalier entre deux étages. */
  at: Point3;
  /** Nouveau chemin à suivre (départ exclu) ; absent si la position est ignorée. */
  path?: Point3[];
  /** Changement d'étage sans escalier dans le modèle : le personnage est posé directement. */
  jump?: boolean;
}

/**
 * Décisions du personnage 3D d'un tracker, sans three.js (ADR-017, démo) : une position reçue est
 * rejetée si elle est impossible à pied depuis la dernière retenue, sinon écrêtée, lissée, puis
 * rejointe par les portes et l'escalier. `tS` est l'heure de **mesure** (s) : des positions reçues
 * en rafale après une coupure ne passent pas pour des sauts.
 */
export class PersonPlanner {
  readonly #filter = new TrajectoryFilter();
  readonly #gate = new WalkGate<Point3>();
  #raw: Point3 | null = null;
  #targetY: number;

  constructor(
    private readonly levels: SiteModelLevel[],
    private readonly graph: () => NavGraph | null,
    private readonly gridAt: (floorY: number) => NavGrid | null,
    start: Point3,
    tS: number,
    s: TrajectorySettings
  ) {
    this.#targetY = start.y;
    this.#filter.push({ x: start.x, z: start.z }, tS, s);
    this.#gate.push(start, tS, s, () => 0);
  }

  receive(measured: Point3, tS: number, pos: Point3, s: TrajectorySettings): PlanStep {
    const raw = this.#raw;
    const graph = s.walls ? this.graph() : null;
    const at = graph ? graph.place(measured) : { ...measured, y: levelOf(this.levels, measured.y).z };
    if (raw && raw.x === measured.x && raw.y === measured.y && raw.z === measured.z) {
      return { verdict: 'duplicate', at };
    }
    this.#raw = measured;
    let verdict: GateVerdict = 'accepted';
    if (graph) {
      const opts = { allowWallCrossing: s.crossWalls };
      verdict = this.#gate.push(at, tS, s, (a, b) => graph.distance(a, b, opts));
      if (verdict === 'rejected') {
        return { verdict, at };
      }
    }
    if (verdict === 'ceded' || Math.abs(at.y - this.#targetY) > 1) {
      this.#filter.reset();
    }
    const f = this.#filter.push({ x: at.x, z: at.z }, tS, s);
    const target = { x: f.x, y: at.y, z: f.z };
    const { path, jump } = graph
      ? this.#route(graph, pos, target, s)
      : { path: [target], jump: Math.abs(target.y - pos.y) > 1 };
    this.#targetY = path[path.length - 1].y;
    return { verdict, at, path, jump };
  }

  #route(graph: NavGraph, from: Point3, target: Point3, s: TrajectorySettings): { path: Point3[]; jump: boolean } {
    const path = graph.route(from, target, { allowWallCrossing: s.crossWalls });
    if (path?.length) {
      return { path, jump: false };
    }
    // Inatteignable (hors du bâtiment à l'étage, pièce sans accès, îlot du modèle) : jamais de ligne
    // droite à travers les murs, arrêt au point atteignable le plus proche sur l'étage.
    const grid = this.gridAt(levelOf(this.levels, from.y).z);
    const sameLevel = Math.abs(from.y - target.y) <= 0.4;
    const reach = grid && sameLevel ? grid.closestReachable(from, target) : null;
    const pts = reach && grid ? (grid.findPath(from, reach) ?? [reach]) : null;
    return pts
      ? { path: pts.map(q => ({ x: q.x, y: from.y, z: q.z })), jump: false }
      : { path: [target], jump: !sameLevel };
  }
}

/**
 * Heure d'une position (ms epoch) quelle que soit sa forme : `Date` (client OpenAPI), texte ISO
 * (message websocket parsé), nombre. `undefined` si absente ou illisible. C'est l'heure de réception
 * par le serveur (contrat-live-geo) : plus juste que l'heure d'arrivée dans le navigateur.
 */
export function timestampMs(v: unknown): number | undefined {
  const ms = v instanceof Date ? v.getTime() : typeof v === 'string' ? Date.parse(v) : typeof v === 'number' ? v : NaN;
  return Number.isFinite(ms) ? ms : undefined;
}
